import React, { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "react-toastify";
import { Check, X, Building } from "lucide-react";
import axios from "axios";

interface Company {
    id: string;
    company_name: string;
    email: string;
    phone_number: string;
    contact_number: string;
    address: string;
    gst_type: string | number;
}

export const CompanyDetailsSection: React.FC = () => {
    const [company, setCompany] = useState<Company | null>(null);
    const [isResetModalOpen, setIsResetModalOpen] = useState(false);
    // Form state
    const [companyName, setCompanyName] = useState("");
    const [email, setEmail] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [contactNumber, setContactNumber] = useState("");
    const [address, setAddress] = useState("");
    const [gstType, setGstType] = useState("");
    const [errors, setErrors] = useState<{
        company_name?: string;
        email?: string;
        phone_number?: string;
        contact_number?: string;
        address?: string;
        gst_type?: string;
    }>({});

    // Memoized GST types
    const gstTypes = useMemo(
        () => [
            { id: "1", type_name: "CGST+SGST" },
            { id: "2", type_name: "IGST" },
        ],
        []
    );

    const isValidEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const fetchCompanyData = useCallback(async () => {
        const token = sessionStorage.getItem("access_token");
        if (!token) {
            toast.error("Authentication error! Please log in.");
            return;
        }

        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/companies`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.status === 200 && response.data.data && response.data.data.length > 0) {
                const companyData = response.data.data[0];
                setCompany(companyData);
                setCompanyName(companyData.company_name || "");
                setEmail(companyData.email || "");
                setPhoneNumber(companyData.phone_number || "");
                setContactNumber(companyData.contact_number || "");
                setAddress(companyData.address || "");
                const gstTypeValue = companyData.gst_type?.toString() || "";
                if (gstTypeValue && !gstTypes.find((type) => type.id === gstTypeValue)) {
                    setErrors((prev) => ({
                        ...prev,
                        gst_type: "Invalid GST type received from server",
                    }));
                }
                setGstType(gstTypeValue);
            } else {
                toast.error(response.data.message || "Failed to fetch company data");
            }
        } catch (error: any) {
            toast.error(error?.response?.data?.message || "Error fetching company data");
            console.error("Fetch error:", error);
        }
    }, [gstTypes]);

    useEffect(() => {
        fetchCompanyData();
    }, [fetchCompanyData]);

    const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const formattedValue = e.target.value.replace(/\D/g, "").slice(0, 10);
        setPhoneNumber(formattedValue);
        if (!formattedValue) {
            setErrors((prev) => ({
                ...prev,
                phone_number: "Phone number is required",
            }));
        } else if (formattedValue.length !== 10) {
            setErrors((prev) => ({
                ...prev,
                phone_number: "Phone number must be exactly 10 digits",
            }));
        } else {
            setErrors((prev) => ({ ...prev, phone_number: undefined }));
        }
    };

    const handleContactNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const formattedValue = e.target.value.replace(/\D/g, "").slice(0, 10);
        setContactNumber(formattedValue);
        if (!formattedValue) {
            setErrors((prev) => ({
                ...prev,
                contact_number: "Contact number is required",
            }));
        } else if (formattedValue.length !== 10) {
            setErrors((prev) => ({
                ...prev,
                contact_number: "Contact number must be exactly 10 digits",
            }));
        } else {
            setErrors((prev) => ({ ...prev, contact_number: undefined }));
        }
    };

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
        field: keyof typeof errors
    ) => {
        const value = e.target.value;
        switch (field) {
            case "company_name":
                setCompanyName(value);
                setErrors((prev) => ({
                    ...prev,
                    company_name: value.trim() === "" ? "Company name is required" : "",
                }));
                break;
            case "email":
                setEmail(value);
                setErrors((prev) => ({
                    ...prev,
                    email:
                        value && !isValidEmail(value)
                            ? "Please enter a valid email address"
                            : value.trim() === ""
                                ? "Email is required"
                                : "",
                }));
                break;
            case "address":
                setAddress(value);
                setErrors((prev) => ({
                    ...prev,
                    address: value.trim() === "" ? "Address is required" : "",
                }));
                break;
            case "gst_type":
                setGstType(value);
                setErrors((prev) => ({
                    ...prev,
                    gst_type: value ? "" : "GST type is required",
                }));
                break;
        }
    };

    const validateForm = () => {
        const newErrors: typeof errors = {};

        if (!companyName) newErrors.company_name = "Company name is required";
        if (!email) newErrors.email = "Email is required";
        else if (!isValidEmail(email)) newErrors.email = "Please enter a valid email address";
        if (!phoneNumber) newErrors.phone_number = "Phone number is required";
        else if (phoneNumber.length !== 10) newErrors.phone_number = "Phone number must be exactly 10 digits";
        if (!contactNumber) newErrors.contact_number = "Contact number is required";
        else if (contactNumber.length !== 10) newErrors.contact_number = "Contact number must be exactly 10 digits";
        if (!address) newErrors.address = "Address is required";
        if (!gstType) newErrors.gst_type = "GST type is required";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const hasChanges = () => {
        return (
            companyName !== (company?.company_name || "") ||
            email !== (company?.email || "") ||
            phoneNumber !== (company?.phone_number || "") ||
            contactNumber !== (company?.contact_number || "") ||
            address !== (company?.address || "") ||
            gstType !== (company?.gst_type?.toString() || "")
        );
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        toast.dismiss();

        if (!validateForm()) {
            toast.error("Please fix the errors before saving");
            return;
        }

        if (!hasChanges()) {
            toast.success("No changes detected to save");
            return;
        }

        const token = sessionStorage.getItem("access_token");
        if (!token) {
            toast.error("Authentication error! Please log in.");
            return;
        }

        const payload = {
            id: company?.id,
            company_name: companyName,
            email,
            phone_number: phoneNumber,
            contact_number: contactNumber,
            address,
            gst_type: gstType,
        };

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/companies/update`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.data.status === 200) {
                toast.success("Company settings saved successfully!");
                fetchCompanyData();
            } else {
                throw new Error("Failed to save company settings");
            }
        } catch (error: any) {
            toast.error(error?.response?.data?.message || "Error saving company settings");
            console.error("Update error:", error);
        }
    };

    const confirmReset = () => {
        setCompanyName(company?.company_name || "");
        setEmail(company?.email || "");
        setPhoneNumber(company?.phone_number || "");
        setContactNumber(company?.contact_number || "");
        setAddress(company?.address || "");
        setGstType(company?.gst_type?.toString() || "");
        setErrors({});
        setIsResetModalOpen(false);
        toast.success("Form reset to original values");
    };

    return (
        <div className="p-4 sm:p-2 bg-white dark:bg-gray-900 rounded-lg">
            <h4 className="text-sm sm:text-base font-semibold text-gray-800 dark:text-white mb-2 flex items-center">
                <div className="w-5 h-5 sm:w-6 sm:h-6 bg-purple-500 rounded-md flex items-center justify-center mr-1">
                    <Building className="w-3 h-3 text-white" />
                </div>
                Company Details
            </h4>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-rose-100 to-pink-200 dark:from-gray-800 dark:to-gray-700 rounded-lg p-3 sm:p-4 border border-rose-200 dark:border-gray-600">
                    <div className="space-y-2">
                        <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-0.5">
                                Company Name <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    value={companyName}
                                    onChange={(e) => handleChange(e, "company_name")}
                                    className={`w-full px-2 py-1.5 bg-white dark:bg-gray-800 border rounded-md focus:ring-2 focus:ring-rose-500
                                     transition-all duration-200 text-gray-700 dark:text-gray-200 text-xs ${errors.company_name ? "border-red-500" : "border-gray-300 dark:border-gray-600"}`}
                                />
                                {companyName && !errors.company_name ? (
                                    <Check className="absolute right-1.5 top-1/2 transform -translate-y-1/2 w-3 h-3 text-green-500" />
                                ) : errors.company_name ? (
                                    <X className="absolute right-1.5 top-1/2 transform -translate-y-1/2 w-3 h-3 text-red-500" />
                                ) : null}
                            </div>
                            {errors.company_name && (
                                <p className="text-red-500 text-xs mt-0.5">{errors.company_name}</p>
                            )}
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-0.5">
                                Email <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => handleChange(e, "email")}
                                    className={`w-full px-2 py-1.5 bg-white dark:bg-gray-800 border rounded-md focus:ring-2 focus:ring-rose-500
                                          transition-all duration-200 text-gray-700 dark:text-gray-200 text-xs ${errors.email ? "border-red-500" : "border-gray-300 dark:border-gray-600"}`}
                                />
                                {email && !errors.email ? (
                                    <Check className="absolute right-1.5 top-1/2 transform -translate-y-1/2 w-3 h-3 text-green-500" />
                                ) : errors.email ? (
                                    <X className="absolute right-1.5 top-1/2 transform -translate-y-1/2 w-3 h-3 text-red-500" />
                                ) : null}
                            </div>
                            {errors.email && <p className="text-red-500 text-xs mt-0.5">{errors.email}</p>}
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-0.5">
                                Phone Number <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <input
                                    type="tel"
                                    value={phoneNumber}
                                    onChange={handlePhoneNumberChange}
                                    className={`w-full px-2 py-1.5 bg-white dark:bg-gray-800 border rounded-md focus:ring-2 focus:ring-rose-500
                                         transition-all duration-200 text-gray-700 dark:text-gray-200 text-xs ${errors.phone_number ? "border-red-500" : "border-gray-300 dark:border-gray-600"}`}
                                />
                                {phoneNumber && !errors.phone_number ? (
                                    <Check className="absolute right-1.5 top-1/2 transform -translate-y-1/2 w-3 h-3 text-green-500" />
                                ) : errors.phone_number ? (
                                    <X className="absolute right-1.5 top-1/2 transform -translate-y-1/2 w-3 h-3 text-red-500" />
                                ) : null}
                            </div>
                            {errors.phone_number && (
                                <p className="text-red-500 text-xs mt-0.5">{errors.phone_number}</p>
                            )}
                        </div>
                    </div>
                </div>
                <div className="bg-gradient-to-br from-teal-50 to-emerald-100 dark:from-gray-800 dark:to-gray-700 rounded-lg p-3 sm:p-4 border border-teal-100 dark:border-gray-600">
                    <div className="space-y-2">
                        <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-0.5">
                                Mobile Number <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <input
                                    type="tel"
                                    value={contactNumber}
                                    onChange={handleContactNumberChange}
                                    className={`w-full px-2 py-1.5 bg-white dark:bg-gray-800 border rounded-md focus:ring-2 focus:ring-teal-500
                                 transition-all duration-200 text-gray-700 dark:text-gray-200 text-xs ${errors.contact_number ? "border-red-500" : "border-gray-300 dark:border-gray-600"}`}
                                />
                                {contactNumber && !errors.contact_number ? (
                                    <Check className="absolute right-1.5 top-1/2 transform -translate-y-1/2 w-3 h-3 text-green-500" />
                                ) : errors.contact_number ? (
                                    <X className="absolute right-1.5 top-1/2 transform -translate-y-1/2 w-3 h-3 text-red-500" />
                                ) : null}
                            </div>
                            {errors.contact_number && (
                                <p className="text-red-500 text-xs mt-0.5">{errors.contact_number}</p>
                            )}
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-0.5">
                                GST Type <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <select
                                    value={gstType}
                                    onChange={(e) => handleChange(e, "gst_type")}
                                    className={`w-full px-2 py-1.5 bg-white dark:bg-gray-800 border rounded-md focus:ring-2 focus:ring-teal-500
                                             transition-all duration-200 text-gray-700 dark:text-gray-200 text-xs ${errors.gst_type ? "border-red-500" : "border-gray-300 dark:border-gray-600"}`}
                                >
                                    <option value="">Select GST Type</option>
                                    {gstTypes.map((type) => (
                                        <option key={type.id} value={type.id}>
                                            {type.type_name}
                                        </option>
                                    ))}
                                </select>
                                {gstType && !errors.gst_type ? (
                                    <Check className="absolute right-1.5 top-1/2 transform -translate-y-1/2 w-3 h-3 text-green-500" />
                                ) : errors.gst_type ? (
                                    <X className="absolute right-1.5 top-1/2 transform -translate-y-1/2 w-3 h-3 text-red-500" />
                                ) : null}
                            </div>
                            {errors.gst_type && <p className="text-red-500 text-xs mt-0.5">{errors.gst_type}</p>}
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-0.5">
                                Address <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <textarea
                                    value={address}
                                    onChange={(e) => handleChange(e, "address")}
                                    rows={3}
                                    className={`w-full px-2 py-1.5 bg-white dark:bg-gray-800 border rounded-md focus:ring-2 focus:ring-teal-500
                                        transition-all duration-200 text-gray-700 dark:text-gray-200 text-xs ${errors.address ? "border-red-500" : "border-gray-300 dark:border-gray-600"}`}
                                />
                                {address && !errors.address ? (
                                    <Check className="absolute right-1.5 top-2 w-3 h-3 text-green-500" />
                                ) : errors.address ? (
                                    <X className="absolute right-1.5 top-2 w-3 h-3 text-red-500" />
                                ) : null}
                            </div>
                            {errors.address && <p className="text-red-500 text-xs mt-0.5">{errors.address}</p>}
                        </div>
                    </div>
                </div>
                <div className="flex justify-end gap-3 mt-3 col-span-2">
                    <button
                        className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-xs font-medium text-white bg-[#2F6AA7] hover:bg-[#265891] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2F6AA7] disabled:opacity-50"
                    >
                        Save Company Settings
                    </button>
                </div>
            </form>
            {isResetModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-4 w-full max-w-sm transform transition-all duration-200 scale-100">
                        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3 text-center">
                            Reset Company Settings
                        </h3>
                        <p className="text-xs text-gray-600 dark:text-gray-300 mb-4 text-center">
                            Are you sure you want to reset the company settings form to default values? This action cannot be undone.
                        </p>
                        <div className="flex justify-center gap-3">
                            <button
                                onClick={() => setIsResetModalOpen(false)}
                                className="px-3 py-1.5 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 transition-all duration-200 text-xs"
                            >
                                No, Cancel
                            </button>
                            <button
                                onClick={confirmReset}
                                className="px-3 py-1.5 bg-green-500 text-white rounded-md hover:bg-green-600 transition-all duration-200 text-xs"
                            >
                                Yes, Reset
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};